import {
  type CalendarZoom,
  type CoWorkerCalendarItem,
  isAbsenceScheduleActivity,
  isAwayTask,
  isCoWorkerCalendarBokaAppointment,
  isCoWorkerCalendarTask,
  isCoWorkerScheduleActivity,
} from "@/features/calendar/types";
import { type Settings } from "@repo/ui";
import { isEmpty } from "lodash";

import {
  ALL_DAY_BLOCK_HEIGHT,
  BOKA_BLOCK_HEIGHT,
  EXTRA_PADDING_HEIGHT,
  HOUR_WIDTH_NORMAL,
  HOUR_WIDTH_ZOOM_OUT,
  NAME_COL_WIDTH,
  TASK_BLOCK_HEIGHT,
} from "../team-calendar/constants";
import { type CoWorkerCalendar } from "../team-calendar/types";

export const sortMeetings = (a: CoWorkerCalendar, b: CoWorkerCalendar) => {
  if (a.meetings.length > 0 && b.meetings.length === 0) {
    return -1;
  }
  if (a.meetings.length === 0 && b.meetings.length > 0) {
    return 1;
  }

  return 0;
};

export const getLaneHeight = (appointments: CoWorkerCalendarItem[][]) => {
  let height = 0;

  for (const row of appointments) {
    height += getHeightOfRowItems(row);
  }
  return (height > 0 ? height : TASK_BLOCK_HEIGHT) + EXTRA_PADDING_HEIGHT;
};

export const getHeightOfRowItems = (meetings: CoWorkerCalendarItem[]) => {
  let maxTop = 0;
  for (const m of meetings) {
    if (m.allDay) {
      if (ALL_DAY_BLOCK_HEIGHT > maxTop) {
        maxTop = ALL_DAY_BLOCK_HEIGHT;
      }
    } else if (m.appointmentType === "task") {
      if (TASK_BLOCK_HEIGHT > maxTop) {
        maxTop = TASK_BLOCK_HEIGHT;
      }
    } else if (m.appointmentType === "appointment") {
      if (BOKA_BLOCK_HEIGHT > maxTop) {
        maxTop = BOKA_BLOCK_HEIGHT;
      }
    } else if (m.appointmentType === "scheduleActivity") {
      if (TASK_BLOCK_HEIGHT > maxTop) {
        maxTop = TASK_BLOCK_HEIGHT;
      }
    }
  }

  return maxTop;
};

export const getZoomHourWidth = (
  {
    zoom,
    zoomWidth,
  }: {
    zoom: CalendarZoom;
    zoomWidth: number;
  },
  totalHours: number
) => {
  if (zoom === "NORMAL") {
    return HOUR_WIDTH_NORMAL;
  }
  const calculatedWidth = (zoomWidth - NAME_COL_WIDTH) / totalHours;

  return Math.max(calculatedWidth, HOUR_WIDTH_ZOOM_OUT);
};

export const isCoworkerAvailable = (
  coworker: CoWorkerCalendar,
  absenceOverridesAvailability?: boolean,
  hideAbsenceBlocks?: boolean
) => {
  // When absence blocks are hidden, a coworker with an active work schedule
  // should always be considered available regardless of absence activities.
  if (hideAbsenceBlocks && coworker.workHours.isWorking) {
    return true;
  }

  if (absenceOverridesAvailability && !isEmpty(coworker.meetings[0])) {
    const scheduleActivities = coworker.meetings[0].filter(
      isCoWorkerScheduleActivity
    );
    const allAbsent =
      scheduleActivities.length > 0 &&
      scheduleActivities.every(isAbsenceScheduleActivity);
    if (allAbsent && !hideAbsenceBlocks) {
      return false;
    }
  }

  if (coworker.workHours.isWorking) {
    return true;
  }

  if (!isEmpty(coworker.meetings[0])) {
    const hasRelevantMeeting = coworker.meetings[0].some((meeting) => {
      if (
        (!absenceOverridesAvailability || hideAbsenceBlocks) &&
        isCoWorkerCalendarTask(meeting) &&
        !isAwayTask(meeting)
      ) {
        return true;
      }
      if (
        isCoWorkerScheduleActivity(meeting) &&
        !isAbsenceScheduleActivity(meeting)
      ) {
        return true;
      }
      if (
        (!absenceOverridesAvailability || hideAbsenceBlocks) &&
        isCoWorkerCalendarBokaAppointment(meeting)
      ) {
        return true;
      }
      return false;
    });

    if (hasRelevantMeeting) {
      return true;
    }
  }

  return false;
};

export const isUploadScheduleEnabled = (
  calendarSettings: Settings["calendar"],
  teamBuCode?: string
) => {
  const teamSetting = calendarSettings?.upload_schedule?.stores?.find(
    (s) => Number(s.unit_id_key) === Number(teamBuCode)
  );

  return teamSetting ? teamSetting?.enable : false;
};

export const isScheduleBlocksEnabled = (
  calendarSettings: Settings["calendar"],
  teamBuCode?: string
) => {
  const teamSetting = calendarSettings?.upload_schedule?.stores?.find(
    (s) => Number(s.unit_id_key) === Number(teamBuCode)
  );

  return (
    teamSetting?.enable_schedule_blocks ||
    teamSetting?.enable_pandp ||
    calendarSettings?.show_people_and_planning_blocks ||
    false
  );
};
